/**
 * Converts a 12-hour hour of day to a 24-hour hour of day.
 * @param hour - The hour of day in 12-hour format
 * @param isPM - Whether the time is in the PM
 * @returns The hour of day in 24-hour format
 * @example
 * format12HourTimeAs24Hour(12, 'AM') // 0
 * format12HourTimeAs24Hour(12, 'PM') // 12
 * format12HourTimeAs24Hour(3, 'PM') // 15
 * format12HourTimeAs24Hour(8, 'AM') // 8
 */
const format12HourTimeAs24Hour = (hour: number, timeOfDay: 'AM' | 'PM' = 'AM'): number => {
  const isPM = timeOfDay.toUpperCase() === 'PM';
  if (!isPM && hour === 12) return 0; // 12am becomes 0
  if (isPM && hour !== 12) return hour + 12; // 1-11pm becomes 13-23
  return hour; // 12pm remains as 12, 1-11am remain as 1-11
};

/**
 * Parses a time string in the format "hh:mm AM/PM" to an object with hour and
 * minute properties in 24-hour format.
 * @param timeString - A time string in the format "hh:mm AM/PM"
 * @returns { hour: number, minute: number }
 * @example
 * parseTimeStringToHoursAndMinutes('12:00 AM') // { hour: 0, minute: 0 }
 * parseTimeStringToHoursAndMinutes('12:00 PM') // { hour: 12, minute: 0 }
 * parseTimeStringToHoursAndMinutes('3:30 PM') // { hour: 15, minute: 30 }
 * parseTimeStringToHoursAndMinutes('11:59 PM') // { hour: 23, minute: 59 }
 */
export const parseTimeStringToHoursAndMinutes = (
  timeString: string,
): { hour: number; minute: number } => {
  // String to hour:minute & AM/PM. EG: '12:00 AM' --> ['12:00', 'AM']
  const [hourMinuteString, ampm] = timeString.split(' ');
  // Hour:Minute to hour & minute. EG: '12:00' --> ['12', '00']
  const [hour12String, minuteString] = hourMinuteString.split(':');
  const minute = parseInt(minuteString, 10); // Convert minute to number
  const hour12 = parseInt(hour12String, 10); // Convert hour to number
  // Convert 12-hour time to 24-hour time
  const hour24 = format12HourTimeAs24Hour(hour12, ampm as 'AM' | 'PM');
  return { hour: hour24, minute };
};
